(() => {
  const SOURCE = "cart-auto-gift";
  const MARKER_KEY = "_rr_auto_gift";
  const MARKER_VALUE = "1";
  const SUPPRESSION_PREFIX = "rr:auto-gift:suppressed";

  const config = window.rrAutoGiftConfig;
  if (!config || config.enabled !== true) return;

  const configuredGiftVariantId = parseInt(config.variantId, 10);
  if (!Number.isFinite(configuredGiftVariantId) || configuredGiftVariantId <= 0)
    return;
  if (
    !window.routes ||
    !window.routes.cart_add_url ||
    !window.routes.cart_change_url ||
    !window.routes.cart_update_url ||
    !window.routes.cart_url
  )
    return;
  if (
    typeof fetchConfig !== "function" ||
    typeof publish !== "function" ||
    typeof PUB_SUB_EVENTS === "undefined"
  )
    return;

  const state = {
    isSyncing: false,
    hasQueuedSync: false,
    previousEligible: false,
    previousAutoPresent: false,
  };

  const buildSuppressionKey = (token) =>
    `${SUPPRESSION_PREFIX}:${token || "unknown"}:${configuredGiftVariantId}`;

  const getSuppressionState = (token) => {
    try {
      return sessionStorage.getItem(buildSuppressionKey(token)) === "1";
    } catch (error) {
      return false;
    }
  };

  const setSuppressionState = (token, isSuppressed) => {
    try {
      if (isSuppressed) {
        sessionStorage.setItem(buildSuppressionKey(token), "1");
      } else {
        sessionStorage.removeItem(buildSuppressionKey(token));
      }
    } catch (error) {}
  };

  const fetchCartJson = async () => {
    const response = await fetch(`${window.routes.cart_url}.js`, {
      method: "GET",
      headers: { Accept: "application/json" },
    });
    if (!response.ok) {
      throw new Error(`cart.js request failed (${response.status})`);
    }
    return response.json();
  };

  const isAutoGiftLine = (line) => {
    const lineVariantId = parseInt(line.variant_id, 10);
    const markerValue = line.properties ? line.properties[MARKER_KEY] : null;
    return (
      lineVariantId === configuredGiftVariantId &&
      String(markerValue) === MARKER_VALUE
    );
  };

  const isEligibleLine = (line) => {
    if (line.gift_card === true) return false;
    const lineVariantId = parseInt(line.variant_id, 10);
    if (lineVariantId === configuredGiftVariantId) return false;
    const lineTotal = Number(line.final_line_price ?? line.line_price ?? 0);
    return lineTotal > 0;
  };

  const getAutoGiftLines = (cart) =>
    (cart.items || []).filter((item) => isAutoGiftLine(item));

  const getAutoGiftQuantity = (autoGiftLines) =>
    autoGiftLines.reduce(
      (sum, line) => sum + (parseInt(line.quantity, 10) || 0),
      0
    );

  const postCartAdd = async () => {
    const response = await fetch(window.routes.cart_add_url, {
      ...fetchConfig(),
      body: JSON.stringify({
        items: [
          {
            id: configuredGiftVariantId,
            quantity: 1,
            properties: { [MARKER_KEY]: MARKER_VALUE },
          },
        ],
      }),
    });
    if (!response.ok) {
      throw new Error(`cart/add.js failed (${response.status})`);
    }
    return response.json();
  };

  const postCartChange = async (lineKey, quantity) => {
    const response = await fetch(window.routes.cart_change_url, {
      ...fetchConfig(),
      body: JSON.stringify({ id: lineKey, quantity }),
    });
    if (!response.ok) {
      throw new Error(`cart/change.js failed (${response.status})`);
    }
    return response.json();
  };

  const postCartUpdate = async (updates) => {
    const response = await fetch(window.routes.cart_update_url, {
      ...fetchConfig(),
      body: JSON.stringify({ updates }),
    });
    if (!response.ok) {
      throw new Error(`cart/update.js failed (${response.status})`);
    }
    return response.json();
  };

  const publishCartUpdate = async (cartData) => {
    return publish(PUB_SUB_EVENTS.cartUpdate, {
      source: SOURCE,
      cartData,
      variantId: configuredGiftVariantId,
    });
  };

  const reconcileCart = async (cart, source) => {
    const autoGiftLines = getAutoGiftLines(cart);
    const autoGiftQuantity = getAutoGiftQuantity(autoGiftLines);
    const eligible = (cart.items || []).some((line) => isEligibleLine(line));
    const autoPresent = autoGiftQuantity > 0;
    const cartToken = cart.token || "unknown";

    if (!eligible) {
      setSuppressionState(cartToken, false);
    }

    if (
      source !== SOURCE &&
      state.previousEligible &&
      state.previousAutoPresent &&
      eligible &&
      !autoPresent
    ) {
      setSuppressionState(cartToken, true);
    }

    const suppressed = getSuppressionState(cartToken);
    let didMutate = false;

    if (!eligible) {
      if (autoGiftQuantity > 0) {
        const updates = {};
        autoGiftLines.forEach((line) => {
          updates[line.key] = 0;
        });
        await postCartUpdate(updates);
        didMutate = true;
      }
    } else if (autoGiftQuantity === 0) {
      if (!suppressed) {
        await postCartAdd();
        didMutate = true;
      }
    } else if (autoGiftQuantity > 1) {
      const [firstLine, ...extraLines] = autoGiftLines;
      if ((parseInt(firstLine.quantity, 10) || 0) !== 1) {
        await postCartChange(firstLine.key, 1);
        didMutate = true;
      }
      if (extraLines.length > 0) {
        const updates = {};
        extraLines.forEach((line) => {
          updates[line.key] = 0;
        });
        await postCartUpdate(updates);
        didMutate = true;
      }
    }

    const resolvedCart = didMutate ? await fetchCartJson() : cart;

    if (didMutate) {
      await publishCartUpdate(resolvedCart);
    }

    state.previousEligible = (resolvedCart.items || []).some((line) =>
      isEligibleLine(line)
    );
    state.previousAutoPresent =
      getAutoGiftQuantity(getAutoGiftLines(resolvedCart)) > 0;
  };

  const runSync = async (source = "unknown") => {
    if (state.isSyncing) {
      state.hasQueuedSync = true;
      return;
    }

    state.isSyncing = true;

    try {
      const cart = await fetchCartJson();
      await reconcileCart(cart, source);
    } catch (error) {
      console.error("Auto gift sync failed", error);
    } finally {
      state.isSyncing = false;
      if (state.hasQueuedSync) {
        state.hasQueuedSync = false;
        return runSync("queued");
      }
    }
  };

  subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
    if (event && event.source === SOURCE) return;
    return runSync(event && event.source ? event.source : "external");
  });

  runSync("init");
})();
